import React, { useState } from "react";
import {
  Button,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Paper,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
  Alert,
  Link,
  Box,
  CircularProgress,
} from "@mui/material";
import useSWR from "swr";
import type { InfoChangeRequest } from "components/infoChangeRequestModel";
import {
  swrKey,
  infoChangeRequestsFetcher,
} from "components/infoChangeRequestsApi";
import { updateInfoChangeRequest } from "components/infoChangeRequestUpdateApi";

export default function InfoChangeRequests() {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [currentRequest, setCurrentRequest] =
    useState<InfoChangeRequest | null>(null);
  const [newStatus, setNewStatus] = useState<boolean>(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState<"success" | "error">(
    "success",
  );
  const [isUpdating, setIsUpdating] = useState(false);

  const { data, isLoading, error, mutate } = useSWR(
    [swrKey, page, rowsPerPage],
    infoChangeRequestsFetcher,
  );

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleStatusChange = (request: InfoChangeRequest, status: boolean) => {
    setCurrentRequest(request);
    setNewStatus(status);
    setConfirmDialogOpen(true);
  };

  const handleConfirmStatusChange = async () => {
    if (currentRequest) {
      try {
        setIsUpdating(true);
        await updateInfoChangeRequest(currentRequest.id, newStatus);
        await mutate(undefined, { revalidate: true });

        // Show snackbar message
        setSnackbarMessage(
          `ステータスが${newStatus ? "処理済み" : "未処理"}に更新されました`,
        );
        setSnackbarSeverity("success");
        setSnackbarOpen(true);
      } catch (error) {
        setSnackbarMessage("ステータスの更新中にエラーが発生しました");
        setSnackbarSeverity("error");
        setSnackbarOpen(true);
      } finally {
        setIsUpdating(false);
        setConfirmDialogOpen(false);
      }
    }
  };

  const handleCloseConfirmDialog = () => {
    if (!isUpdating) {
      setConfirmDialogOpen(false);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbarOpen(false);
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Typography variant="h4" component="h1" gutterBottom>
        会員情報変更申請一覧
      </Typography>

      {/* Requests Table */}
      <TableContainer component={Paper}>
        <Table
          stickyHeader
          sx={{ minWidth: 650 }}
          aria-label="information change requests table"
        >
          <TableHead>
            <TableRow>
              <TableCell style={{ minWidth: 140 }}>申請番号</TableCell>
              <TableCell style={{ minWidth: 120 }}>メールアドレス</TableCell>
              <TableCell style={{ minWidth: 120 }}>登録者名</TableCell>
              <TableCell style={{ minWidth: 120 }}>登録者フリガナ</TableCell>
              <TableCell style={{ minWidth: 120 }}>電話番号</TableCell>
              <TableCell style={{ minWidth: 120 }}>FAX番号</TableCell>
              <TableCell style={{ minWidth: 120 }}>退会申請</TableCell>
              <TableCell style={{ minWidth: 120 }}>会員詳細</TableCell>
              <TableCell style={{ minWidth: 120 }}>ステータス</TableCell>
              <TableCell style={{ minWidth: 160 }} align="center">
                操作
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading && (
              <TableRow>
                <TableCell colSpan={11} align="center">
                  <Box
                    sx={{ display: "flex", justifyContent: "center", mt: 4 }}
                  >
                    <CircularProgress />
                  </Box>
                </TableCell>
              </TableRow>
            )}

            {error && (
              <TableRow>
                <TableCell colSpan={11} align="center">
                  <Box sx={{ mt: 2, mb: 2, color: "error.main" }}>
                    <Typography variant="h6" color="error">
                      エラーが発生しました
                    </Typography>
                    <Typography variant="body2">
                      {error.message || "データの取得中に問題が発生しました。"}
                    </Typography>
                  </Box>
                </TableCell>
              </TableRow>
            )}

            {data &&
              data.infoChangeRequests &&
              data.infoChangeRequests.map((request) => (
                <TableRow
                  key={request.id}
                  sx={{
                    color: request.isProcessed ? "text.disabled" : "inherit",
                    "& .MuiTableCell-root": {
                      color: "inherit",
                    },
                  }}
                >
                  <TableCell style={{ wordBreak: "break-all" }}>{request.requestNumber}</TableCell>
                  <TableCell style={{ wordBreak: "break-all" }}>{request.email}</TableCell>
                  <TableCell style={{ wordBreak: "break-all" }}>{request.applicantName}</TableCell>
                  <TableCell style={{ wordBreak: "break-all" }}>{request.applicantNameKana}</TableCell>
                  <TableCell style={{ wordBreak: "break-all" }}>{request.phoneNumber}</TableCell>
                  <TableCell style={{ wordBreak: "break-all" }}>{request.faxNumber}</TableCell>
                  <TableCell style={{ wordBreak: "break-all" }}>{request.requestDelete}</TableCell>
                  <TableCell>
                    <Link
                      href={`/users/${request.userId}/details`}
                      target="_blank"
                      underline="hover"
                    >
                      会員詳細
                    </Link>
                  </TableCell>
                  <TableCell>
                    <Typography component="span">
                      {request.isProcessed ? "処理済み" : "未処理"}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Box
                      sx={{ display: "flex", gap: 1, justifyContent: "center" }}
                    >
                      {!request.isProcessed && (
                        <Button
                          variant="contained"
                          color="primary"
                          size="small"
                          onClick={() => handleStatusChange(request, true)}
                          disabled={request.isProcessed}
                        >
                          処理済みにする
                        </Button>
                      )}
                      {request.isProcessed && (
                        <Button
                          variant="outlined"
                          color="primary"
                          size="small"
                          onClick={() => handleStatusChange(request, false)}
                          disabled={!request.isProcessed}
                        >
                          未処理に戻す
                        </Button>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[10]}
        component="div"
        count={data?.totalCount || 0}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        labelRowsPerPage="表示件数:"
      />

      {/* Confirmation Dialog */}
      <Dialog
        open={confirmDialogOpen}
        onClose={handleCloseConfirmDialog}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">ステータス変更の確認</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            ステータスを{newStatus ? "処理済み" : "未処理"}に変更しますか？
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleCloseConfirmDialog}
            color="primary"
            disabled={isUpdating}
          >
            キャンセル
          </Button>
          <Button
            onClick={handleConfirmStatusChange}
            color="primary"
            autoFocus
            disabled={isUpdating}
          >
            {isUpdating ? (
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <CircularProgress size={20} sx={{ mr: 1 }} />
                更新中...
              </Box>
            ) : (
              "確認"
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbarSeverity}
          sx={{ width: "100%" }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Container>
  );
}
